import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import "./Campaign.css";
import "./CampDailyReport.css";
import { getCampReports } from "../../api/admin/dashboard";
import CustomDatePicker from "../resuableComponents/CustomDatePicker";

const COLUMNS = [
  { key: "actualCampsDone", label: "Camps done" },
  { key: "campPatientCount", label: "Camp patients" },
  { key: "prakritiReportsFiled", label: "Reports filed" },
  { key: "prakritiReportsGenerated", label: "Reports generated" },
  { key: "actualPatientsVisited", label: "Patients visited" },
  { key: "demoVisitsDone", label: "Demo visits" },
];

const COLUMN_COUNT = 4 + COLUMNS.length;

const reportDay = (report) => report.reportDate || report.date || report.createdAt;

const CampDailyReports = () => {
  const [date, setDate] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const hasDateFilter = Boolean(date?.isValid?.());
  const dateValue = hasDateFilter ? date.format("YYYY-MM-DD") : null;

  useEffect(() => {
    const loadReports = async () => {
      setLoading(true);
      try {
        const response = await getCampReports(dateValue);
        setReports(response.data?.data?.reports || []);
      } catch (error) {
        console.error("Failed to load camp reports", error);
        setReports([]);
        if (error.response?.status !== 404) {
          toast.error(error.response?.data?.message || "Could not load camp reports.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [dateValue]);

  return (
    <div className="camp-surface">
      <div className="camp-shell daily-report-shell">
        <header className="camp-hero daily-report-hero">
          <div className="camp-hero-copy">
            <p className="camp-chip">Sales submissions</p>
            <h1>
              Camp reports
              <span>
                {hasDateFilter
                  ? ` for ${date.format("DD MMM YYYY")}.`
                  : " — all dates."}
              </span>
            </h1>
            <p className="daily-report-note">
              Showing every submitted report. Pick a date if you want to see one day only.
            </p>
          </div>
          <div className="daily-date-field">
            <span>Filter by date</span>
            <div className="daily-date-row">
              <CustomDatePicker
                value={hasDateFilter ? date : null}
                onChange={(next) => setDate(next && next.isValid() ? next : null)}
                maxDate={dayjs()}
                placeholder="All dates"
                className="daily-date-input"
              />
              {hasDateFilter && (
                <button type="button" className="daily-date-clear" onClick={() => setDate(null)}>
                  Clear
                </button>
              )}
            </div>
          </div>
        </header>

        <section className="camp-table-wrapper">
          <table className="camp-table daily-report-table">
            <thead>
              <tr>
                <th>Sales person</th>
                <th>Branch</th>
                <th>Report date</th>
                <th>Updated</th>
                {COLUMNS.map((column) => (
                  <th key={column.key} className="daily-num-head">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="camp-skeleton-row">
                    {Array.from({ length: COLUMN_COUNT }).map((__, cell) => (
                      <td key={cell}>
                        <div className="camp-skeleton-cell" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : reports.length > 0 ? (
                reports.map((report) => (
                  <tr key={report._id}>
                    <td className="camp-name">{report.submitterName || "—"}</td>
                    <td>
                      <span className="camp-badge camp-campaign" title={report.branch || ""}>
                        {report.branch || "—"}
                      </span>
                    </td>
                    <td className="camp-nowrap">
                      {reportDay(report) ? dayjs(reportDay(report)).format("DD MMM YYYY") : "—"}
                    </td>
                    <td className="camp-nowrap">
                      {report.updatedAt ? dayjs(report.updatedAt).format("hh:mm A") : "—"}
                    </td>
                    {COLUMNS.map((column) => (
                      <td key={column.key} className="daily-num">
                        <span className="daily-count">{report[column.key] ?? 0}</span>
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="camp-empty">
                    <div className="camp-empty-inner">
                      <strong>
                        {hasDateFilter
                          ? "No camp reports for this date"
                          : "No camp reports yet"}
                      </strong>
                      <p>
                        {hasDateFilter
                          ? "Try another date, or clear the filter to see every report."
                          : "Submissions from sales staff will appear here once they file a report."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
};

export default CampDailyReports;
